import api, { fetchData } from './api';
import { Vendor, VendorSchedule, VendorMetadata, VendorDelta, DateRange } from '../types/types';
import { logger } from './logger';

// GEOJSON interfaces to match backend response structure
interface VendorProperties {
  name: string;
  category: string;
  schedule: VendorSchedule;
  localized_schedule?: VendorSchedule & {
    is_overnight?: string;
  };
  timezone_info?: {
    name: string;
    display_name: string;
    region?: string;
  };
  metadata: VendorMetadata;
  uses_gps: boolean;
  gps_device: string | null;
  start_date: string | null;
  expiry: string | null;
  uuid: string;
  location: {
    type: 'Point';
    coordinates: [number, number];
  } | null;
  // For expanded endpoint
  display_date?: string;
  display_id?: string;
  is_own_event?: boolean;
  visibility?: string;
}

interface VendorFeature {
  id: number;
  type: 'Feature';
  properties: VendorProperties;
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
}

interface VendorCollection {
  type: 'FeatureCollection';
  features: VendorFeature[];
}

// Define location update request/response types
interface LocationUpdate {
  latitude: number;
  longitude: number;
}

interface LocationResponse {
  latitude: number;
  longitude: number;
  is_gps: boolean;
}

// Define map bounds interface
export interface MapBounds {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

// Define date range filter interface with map bounds
export interface DateRangeFilter {
  startDate?: Date | null;
  endDate?: Date | null;
  mapBounds?: MapBounds | null;
}

export interface CuratedListMeta {
  uuid: string;
  title: string;
  description: string;
  display_name: string;
  item_count: number;
}

interface ExpandedVendorCollection {
  type: 'FeatureCollection';
  features: VendorFeature[];
  curated_lists: CuratedListMeta[];
}

// Helper function to determine API prefix based on environment
// If REACT_APP_API_URL already ends with /api, we don't need to add it again
const getApiPrefix = () => {
  const baseUrl = process.env.REACT_APP_API_URL || '';
  // Check if the base URL already includes /api
  const endsWithApi = baseUrl.endsWith('/api');
  // If baseUrl ends with /api, return empty string (no additional prefix)
  // Otherwise return /api/ to be added to endpoints
  return endsWithApi ? '' : '/api';
};

// Get all vendors with optional date range and map bounds filtering
export const getAllVendors = async (filters?: DateRangeFilter): Promise<Vendor[]> => {
  try {
    // Build query params for filtering
    const params = new URLSearchParams();

    // get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    params.append('user_timezone', userTimezone);
    logger.log('📤 vendorApi sending user_timezone:', userTimezone);

    if (filters) {
      if (filters.startDate) {
        const startDateStr = filters.startDate.toISOString().split('T')[0];
        params.append('start_date', startDateStr);
        logger.log('📤 vendorApi sending start_date:', startDateStr);
      }

      if (filters.endDate) {
        const endDateStr = filters.endDate.toISOString().split('T')[0];
        params.append('end_date', endDateStr);
        logger.log('📤 vendorApi sending end_date:', endDateStr);
      }

      if (filters.mapBounds) {
        params.append('min_lng', filters.mapBounds.minLng.toString());
        params.append('min_lat', filters.mapBounds.minLat.toString());
        params.append('max_lng', filters.mapBounds.maxLng.toString());
        params.append('max_lat', filters.mapBounds.maxLat.toString());
      }
    }

    // Get the correct API prefix
    const apiPrefix = getApiPrefix();

    // Use the helper function for making the request with the parameters
    const response = await fetchData<VendorCollection>(
      `${apiPrefix}/vendors/`,
      params.toString() ? Object.fromEntries(params.entries()) : undefined
    );

    // Add defensive check to prevent "Cannot read properties of undefined" error
    if (!response || !response.features) {
      logger.error('Invalid response format from vendors API:', response);
      return [];
    }

    return response.features.map(feature => ({
      id: feature.id,
      uuid: feature.properties.uuid, // Include UUID in the response
      name: feature.properties.name,
      category: feature.properties.category,
      coordinates: feature.properties.location?.coordinates || feature.geometry.coordinates,
      schedule: feature.properties.schedule,
      localized_schedule: feature.properties.localized_schedule,
      timezone_info: feature.properties.timezone_info,
      metadata: feature.properties.metadata,
      uses_gps: feature.properties.uses_gps,
      gps_device: feature.properties.gps_device,
      start_date: feature.properties.start_date,
      expiry: feature.properties.expiry,
    }));
  } catch (error) {
    logger.error('Error fetching vendors:', error);
    // Return empty array on error rather than throwing to prevent app crashes
    return [];
  }
};

// Get all vendors EXPANDED into individual date instances (new endpoint)
// This replaces the frontend expansion logic with backend expansion
export const getAllVendorsExpanded = async (
  filters?: DateRangeFilter
): Promise<{
  vendors: Vendor[];
  curatedLists: CuratedListMeta[];
}> => {
  try {
    // Build query params for filtering
    const params = new URLSearchParams();

    // get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    params.append('user_timezone', userTimezone);
    logger.log('📤 vendorApi (expanded) sending user_timezone:', userTimezone);

    if (filters) {
      if (filters.startDate) {
        const startDateStr = filters.startDate.toISOString().split('T')[0];
        params.append('start_date', startDateStr);
        logger.log('📤 vendorApi (expanded) sending start_date:', startDateStr);
      }

      if (filters.endDate) {
        const endDateStr = filters.endDate.toISOString().split('T')[0];
        params.append('end_date', endDateStr);
        logger.log('📤 vendorApi (expanded) sending end_date:', endDateStr);
      }

      if (filters.mapBounds) {
        params.append('min_lng', filters.mapBounds.minLng.toString());
        params.append('min_lat', filters.mapBounds.minLat.toString());
        params.append('max_lng', filters.mapBounds.maxLng.toString());
        params.append('max_lat', filters.mapBounds.maxLat.toString());
      }
    }

    // Get the correct API prefix
    const apiPrefix = getApiPrefix();

    const response = await fetchData<ExpandedVendorCollection>(
      `${apiPrefix}/vendors/expanded/`,
      params.toString() ? Object.fromEntries(params.entries()) : undefined
    );

    if (!response || !response.features) {
      logger.error('Invalid response format from vendors/expanded API:', response);
      return { vendors: [], curatedLists: [] };
    }

    const vendors = response.features.map(
      feature =>
        ({
          id: feature.id,
          uuid: feature.properties.uuid,
          name: feature.properties.name,
          category: feature.properties.category,
          coordinates: feature.properties.location?.coordinates || feature.geometry.coordinates,
          schedule: feature.properties.schedule,
          localized_schedule: feature.properties.localized_schedule,
          timezone_info: feature.properties.timezone_info,
          metadata: feature.properties.metadata,
          uses_gps: feature.properties.uses_gps,
          gps_device: feature.properties.gps_device,
          start_date: feature.properties.start_date,
          expiry: feature.properties.expiry,
          // Backend provides these for expanded vendors; time added to prevent UTC conversion
          displayDate: feature.properties.display_date
            ? new Date(feature.properties.display_date + 'T00:00:00')
            : undefined,
          displayID: feature.properties.display_id,
          is_own_event: feature.properties.is_own_event,
          visibility: feature.properties.visibility,
        }) as any
    ); // Cast to any to allow extra properties temporarily
    return {
      vendors,
      curatedLists: response.curated_lists || [],
    };
  } catch (error) {
    logger.error('Error fetching expanded vendors:', error);
    return { vendors: [], curatedLists: [] };
  }
};

const mapFeatureToVendor = (feature: VendorFeature): Vendor => ({
  id: feature.id,
  uuid: feature.properties.uuid,
  name: feature.properties.name,
  category: feature.properties.category,
  coordinates: feature.properties.location?.coordinates || feature.geometry.coordinates,
  schedule: feature.properties.schedule,
  localized_schedule: feature.properties.localized_schedule,
  timezone_info: feature.properties.timezone_info,
  metadata: feature.properties.metadata,
  uses_gps: feature.properties.uses_gps,
  gps_device: feature.properties.gps_device,
  start_date: feature.properties.start_date,
  expiry: feature.properties.expiry,
  displayDate: feature.properties.display_date
    ? new Date(feature.properties.display_date + 'T00:00:00')
    : undefined,
  displayID: feature.properties.display_id,
  is_own_event: feature.properties.is_own_event,
  visibility: feature.properties.visibility as Vendor['visibility'],
});

interface DeltaResponse {
  added: VendorFeature[];
  updated: VendorFeature[];
  removed: number[];
  server_time: string;
}

export const getVendorUpdates = async (
  since: string,
  viewport: import('../types/types').MapBounds | null,
  dateRange: DateRange,
  timezone: string
): Promise<VendorDelta> => {
  const params = new URLSearchParams({ since, user_timezone: timezone });
  params.append('start_date', dateRange.startDate.toISOString().split('T')[0]);
  params.append('end_date', dateRange.endDate.toISOString().split('T')[0]);
  if (viewport) {
    params.append('min_lng', viewport.minLng.toString());
    params.append('min_lat', viewport.minLat.toString());
    params.append('max_lng', viewport.maxLng.toString());
    params.append('max_lat', viewport.maxLat.toString());
  }
  const apiPrefix = getApiPrefix();
  const data = await fetchData<DeltaResponse>(
    `${apiPrefix}/vendors/updates/`,
    Object.fromEntries(params.entries())
  );
  return {
    added: (data.added || []).map(mapFeatureToVendor),
    updated: (data.updated || []).map(mapFeatureToVendor),
    removed: data.removed || [],
    server_time: data.server_time,
  };
};

// Get a single vendor by numeric ID (maintained for backward compatibility)
export const getVendor = async (id: number): Promise<Vendor | null> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.get(`${apiPrefix}/vendors/${id}/`);
    const feature = response.data;

    if (!feature) {
      return null;
    }

    return {
      id: feature.id,
      uuid: feature.properties.uuid,
      name: feature.properties.name,
      category: feature.properties.category,
      coordinates: feature.properties.location?.coordinates || feature.geometry.coordinates,
      schedule: feature.properties.schedule,
      localized_schedule: feature.properties.localized_schedule,
      timezone_info: feature.properties.timezone_info,
      metadata: feature.properties.metadata,
      uses_gps: feature.properties.uses_gps,
      gps_device: feature.properties.gps_device,
      start_date: feature.properties.start_date,
      expiry: feature.properties.expiry,
    };
  } catch (error) {
    logger.error(`Error fetching vendor ${id}:`, error);
    return null;
  }
};

// Get a single vendor by UUID (new preferred method)
export const getVendorByUuid = async (uuid: string): Promise<Vendor | null> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.get(`${apiPrefix}/vendors/uuid/${uuid}/`);
    const feature = response.data;

    if (!feature) {
      return null;
    }

    return {
      id: feature.id,
      uuid: feature.properties.uuid,
      name: feature.properties.name,
      category: feature.properties.category,
      coordinates: feature.properties.location?.coordinates || feature.geometry.coordinates,
      schedule: feature.properties.schedule,
      metadata: feature.properties.metadata,
      uses_gps: feature.properties.uses_gps,
      gps_device: feature.properties.gps_device,
      start_date: feature.properties.start_date,
      expiry: feature.properties.expiry,
    };
  } catch (error) {
    logger.error(`Error fetching vendor with UUID ${uuid}:`, error);
    return null;
  }
};

// Update location for a vendor (protected endpoint - requires authentication)
export const updateVendorLocation = async (
  vendorId: number | string,
  location: LocationUpdate
): Promise<any> => {
  try {
    const apiPrefix = getApiPrefix();

    // If vendorId is a string and looks like a UUID, use the UUID endpoint
    if (typeof vendorId === 'string' && vendorId.includes('-')) {
      const response = await api.post(
        `${apiPrefix}/vendors/uuid/${vendorId}/update_location/`,
        location
      );
      return response.data;
    } else {
      // Otherwise use the numeric ID endpoint
      const response = await api.post(
        `${apiPrefix}/vendors/${vendorId}/update_location/`,
        location
      );
      return response.data;
    }
  } catch (error) {
    logger.error(`Error updating location for vendor ${vendorId}:`, error);
    throw error;
  }
};

// Create a new vendor (protected endpoint - requires authentication)
export const createVendor = async (vendorData: Omit<Vendor, 'id' | 'uuid'>): Promise<Vendor> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.post(`${apiPrefix}/vendors/`, vendorData);
    return response.data;
  } catch (error) {
    logger.error('Error creating vendor:', error);
    throw error;
  }
};

// Update a vendor (protected endpoint - requires authentication)
// Accepts either numeric ID or UUID
export const updateVendor = async (
  idOrUuid: number | string,
  vendorData: Partial<Vendor>
): Promise<Vendor> => {
  try {
    const apiPrefix = getApiPrefix();

    // If it's a string and looks like a UUID, use the UUID endpoint
    if (typeof idOrUuid === 'string' && idOrUuid.includes('-')) {
      const response = await api.patch(`${apiPrefix}/vendors/uuid/${idOrUuid}/`, vendorData);
      return response.data;
    } else {
      // Otherwise use the numeric ID endpoint
      const response = await api.patch(`${apiPrefix}/vendors/${idOrUuid}/`, vendorData);
      return response.data;
    }
  } catch (error) {
    logger.error(`Error updating vendor ${idOrUuid}:`, error);
    throw error;
  }
};

// Delete a vendor (protected endpoint - requires authentication)
// This now uses soft delete
export const deleteVendor = async (idOrUuid: number | string): Promise<void> => {
  try {
    const apiPrefix = getApiPrefix();

    // If it's a string and looks like a UUID, use the UUID endpoint
    if (typeof idOrUuid === 'string' && idOrUuid.includes('-')) {
      await api.delete(`${apiPrefix}/vendors/uuid/${idOrUuid}/`);
    } else {
      // Otherwise use the numeric ID endpoint
      await api.delete(`${apiPrefix}/vendors/${idOrUuid}/`);
    }
  } catch (error) {
    logger.error(`Error deleting vendor ${idOrUuid}:`, error);
    throw error;
  }
};

// Restore a soft-deleted vendor
export const restoreVendor = async (idOrUuid: number | string): Promise<Vendor> => {
  try {
    const apiPrefix = getApiPrefix();

    // If it's a string and looks like a UUID, use the UUID endpoint
    if (typeof idOrUuid === 'string' && idOrUuid.includes('-')) {
      const response = await api.post(`${apiPrefix}/vendors/uuid/${idOrUuid}/restore/`);
      return response.data;
    } else {
      // Otherwise use the numeric ID endpoint
      const response = await api.post(`${apiPrefix}/vendors/${idOrUuid}/restore/`);
      return response.data;
    }
  } catch (error) {
    logger.error(`Error restoring vendor ${idOrUuid}:`, error);
    throw error;
  }
};

export const getBatchVendorLocations = async (
  vendorIds: number[]
): Promise<Record<number, LocationResponse>> => {
  if (!vendorIds.length) {
    logger.log('No vendor IDs provided to getBatchVendorLocations');
    return {};
  }

  try {
    const apiPrefix = getApiPrefix();
    logger.log(
      `Making batch request to ${apiPrefix}/vendors/batch_locations/?ids=${vendorIds.join(',')}`
    );
    const response = await api.get(
      `${apiPrefix}/vendors/batch_locations/?ids=${vendorIds.join(',')}`
    );
    logger.log('Batch response:', response.data);
    return response.data;
  } catch (error) {
    logger.error(`Error fetching batch locations:`, error);
    return {};
  }
};
